import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Car, Trash2, BookOpen, ChevronDown, FileDown, Mail } from "lucide-react";
import { BookingRow } from "@/components/BookingRow";

export const GarageVehicleRow = ({ v, log, isOpen, onToggleLog, onRemove, userEmail }) => {
  const [pdfBusy, setPdfBusy] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const hasCompleted = log.some((b) => b.status === "completed");

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await api.get(`/customer/vehicles/${v.id}/logbook.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `service-history-${v.make}-${v.model}-${v.year}.pdf`.toLowerCase().replace(/\s+/g, "-");
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Couldn't generate the PDF — please try again.");
    } finally {
      setPdfBusy(false);
    }
  };

  const sendLogbookEmail = async () => {
    setEmailSending(true);
    try {
      await api.post(`/customer/vehicles/${v.id}/email-logbook`, { to_email: emailTo });
      toast.success(`Service history emailed to ${emailTo}`);
      setEmailOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't send the email — please try again.");
    } finally {
      setEmailSending(false);
    }
  };

  const actionBtn = "inline-flex items-center gap-1.5 px-3 py-1.5 border text-[11px] font-mono uppercase tracking-widest transition-colors";

  return (
    <div className="bg-zinc-950">
      <div data-testid={`saved-vehicle-${v.id}`} className="p-4 flex items-center gap-4 flex-wrap">
        <span className="h-9 w-9 flex items-center justify-center bg-zinc-900 shrink-0"><Car className="h-4 w-4 text-brand-red" /></span>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white font-semibold truncate">{v.year} {v.make} {v.model}</div>
          <div className="text-xs text-zinc-500">{v.rego ? `${v.rego} · ${v.state}` : v.state}</div>
        </div>
        {log.length > 0 && (
          <button data-testid={`logbook-toggle-${v.id}`} onClick={onToggleLog}
            className={`${actionBtn} ${isOpen ? "border-brand-red text-brand-red" : "border-zinc-700 text-zinc-300 hover:border-brand-red hover:text-brand-red"}`}>
            <BookOpen className="h-3 w-3" /> Logbook ({log.length})
            <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? "rotate-180" : ""}`} />
          </button>
        )}
        {hasCompleted && (
          <>
            <button data-testid={`logbook-pdf-${v.id}`} onClick={downloadPdf} disabled={pdfBusy}
              title="Download service history PDF"
              className={`${actionBtn} border-zinc-700 text-zinc-300 hover:border-brand-red hover:text-brand-red disabled:opacity-50`}>
              <FileDown className="h-3 w-3" /> {pdfBusy ? "…" : "PDF"}
            </button>
            <button data-testid={`logbook-email-${v.id}`}
              onClick={() => { setEmailOpen(!emailOpen); setEmailTo(userEmail); }}
              title="Email service history PDF"
              className={`${actionBtn} ${emailOpen ? "border-brand-red text-brand-red" : "border-zinc-700 text-zinc-300 hover:border-brand-red hover:text-brand-red"}`}>
              <Mail className="h-3 w-3" /> Email
            </button>
          </>
        )}
        <button data-testid={`delete-vehicle-${v.id}`} onClick={onRemove} aria-label="Remove vehicle"
          className="p-2 text-zinc-600 hover:text-brand-red transition-colors">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      {emailOpen && (
        <form data-testid={`email-logbook-form-${v.id}`} onSubmit={(e) => { e.preventDefault(); sendLogbookEmail(); }}
          className="border-t border-zinc-900 p-4 flex flex-wrap items-center gap-3">
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Send service history to</span>
          <input data-testid={`email-logbook-input-${v.id}`} type="email" required value={emailTo}
            onChange={(e) => setEmailTo(e.target.value)} placeholder="buyer@example.com"
            className="ta-input flex-1 min-w-[220px]" />
          <button data-testid={`email-logbook-send-${v.id}`} disabled={emailSending || !emailTo}
            className="px-4 py-2 bg-brand-red text-white font-mono text-[11px] uppercase tracking-widest hover:bg-[#B91C2B] disabled:opacity-50">
            {emailSending ? "Sending…" : "Send PDF"}
          </button>
        </form>
      )}
      {isOpen && (
        <div data-testid={`logbook-${v.id}`} className="border-t border-zinc-900 space-y-px bg-zinc-900">
          {log.map((b) => <BookingRow key={b.id} b={b} />)}
        </div>
      )}
    </div>
  );
};
